/**
 * Read-only share control for the Career Clarity 360 Passport.
 *
 * The link carries a frozen snapshot of the sealed report. A recipient can
 * read every page and print it; they cannot sign in as the learner, re-run
 * the walk, or change a single number. The owner can revoke at any time.
 */
import { useState } from "react";
import { Share2, Copy, Check, Loader2, Link2Off, ExternalLink, Mail, MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { Dossier } from "../dossier";

export default function ShareDossierLink({ dossier }: { dossier: Dossier }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const shareUrl = token ? `${window.location.origin}/d/${token}` : "";

  const create = async () => {
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("dossier-share", {
        body: {
          action: "create",
          dossier,
          report_hash: dossier.reportHash,
          band: dossier.band,
          attempt_ref: dossier.attemptRef,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setToken(data.token);
      setOpen(true);
    } catch (e) {
      toast({
        title: "Couldn't create the link",
        description: e instanceof Error ? e.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setRevoking(true);
    try {
      const { data, error } = await supabase.functions.invoke("dossier-share", {
        body: { action: "revoke", token },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setToken(null);
      setOpen(false);
      toast({ title: "Link revoked", description: "Anyone holding it now sees nothing." });
    } catch (e) {
      toast({
        title: "Couldn't revoke",
        description: e instanceof Error ? e.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setRevoking(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({ title: "Link copied" });
  };

  return (
    <>
      <button
        type="button"
        onClick={create}
        disabled={busy}
        className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-2xl border border-border
          text-sm font-medium hover:bg-muted disabled:opacity-60
          focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {busy
          ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          : <Share2 className="h-4 w-4" aria-hidden="true" />}
        Share read-only
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Share2 className="h-5 w-5 text-primary" aria-hidden="true" />
              Your read-only link
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Anyone with this link can read your dossier, exactly as it is sealed now
            {dossier.reportHash ? ` (seal ${dossier.reportHash.slice(0, 12)})` : ""}.
            They can't sign in, change anything, or see the rest of your account.
          </p>
          <div className="flex gap-2">
            <input
              readOnly
              value={shareUrl}
              aria-label="Read-only share link"
              className="flex-1 min-h-[44px] rounded-xl border border-border bg-muted/40 px-3
                font-mono text-xs"
            />
            <button
              type="button"
              onClick={copy}
              aria-label="Copy link"
              className="min-h-[44px] min-w-[44px] grid place-items-center rounded-xl border border-border
                hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {copied
                ? <Check className="h-4 w-4 text-primary" aria-hidden="true" />
                : <Copy className="h-4 w-4" aria-hidden="true" />}
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2 pt-1">
            <button
              type="button"
              onClick={() => {
                const text = encodeURIComponent(
                  `Here's my WeKIT™ Career Clarity 360 Passport — what fits me, and why: ${shareUrl}`,
                );
                window.open(`https://wa.me/?text=${text}`, "_blank", "noopener");
              }}
              className="inline-flex items-center justify-center gap-2 min-h-[44px] rounded-xl
                bg-primary text-primary-foreground text-sm font-medium hover:opacity-90
                focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <MessageCircle className="h-4 w-4" aria-hidden="true" /> WhatsApp
            </button>
            <a
              href={`mailto:?subject=${encodeURIComponent("My WeKIT™ Career Clarity 360 Passport")}&body=${encodeURIComponent(
                `Hi,\n\nHere's my WeKIT™ Career Clarity 360 Passport. It's read-only:\n\n${shareUrl}\n`,
              )}`}
              className="inline-flex items-center justify-center gap-2 min-h-[44px] rounded-xl
                border border-border text-sm font-medium hover:bg-muted
                focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <Mail className="h-4 w-4" aria-hidden="true" /> Email
            </a>
          </div>

          <div className="flex gap-2 pt-2">
            <a
              href={shareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-2 min-h-[44px] rounded-xl
                border border-border text-sm hover:bg-muted
                focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              Preview <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
            <button
              type="button"
              onClick={revoke}
              disabled={revoking}
              className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-xl text-sm
                text-destructive hover:bg-destructive/10 disabled:opacity-60
                focus:outline-none focus-visible:ring-2 focus-visible:ring-destructive"
            >
              {revoking
                ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                : <Link2Off className="h-4 w-4" aria-hidden="true" />}
              Revoke
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
